import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, GripVertical, FileText, Loader2, ArrowUpDown } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import pageService from "../../services/pageService";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { cn } from "../../lib/utils/utils";

const PageReorder = () => {
  const navigate = useNavigate();
  const [capabilities, setCapabilities] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [generalPages, setGeneralPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const response = await pageService.getPages();
      if (response.success) {
        const pages = response.data;
        const caps = pages.filter((p) => p.category === "Capabilities");
        const inds = pages.filter((p) => p.category === "Industries");
        const general = pages.filter((p) => p.category === "General");
        setCapabilities(caps);
        setIndustries(inds);
        setGeneralPages(general);
      } else {
        toast.error("Failed to load pages");
      }
    } catch (error) {
      toast.error("Failed to fetch pages");
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    const category = source.droppableId;

    const pagesByCategory = {
      Capabilities: capabilities,
      Industries: industries,
      General: generalPages,
    };
    const settersByCategory = {
      Capabilities: setCapabilities,
      Industries: setIndustries,
      General: setGeneralPages,
    };

    const listToReorder = [...(pagesByCategory[category] || [])];
    const [reorderedItem] = listToReorder.splice(source.index, 1);
    listToReorder.splice(destination.index, 0, reorderedItem);

    // Optimistically update UI sort orders
    const updatedList = listToReorder.map((item, index) => ({
      ...item,
      sort_order: index + 1,
    }));

    settersByCategory[category](updatedList);

    // Build the payload for bulk update
    const otherPages = Object.entries(pagesByCategory)
      .filter(([name]) => name !== category)
      .flatMap(([, pages]) => pages);
    const allOrders = [
      ...updatedList.map((item) => ({ id: item.id, sort_order: item.sort_order })),
      ...otherPages.map((item) => ({ id: item.id, sort_order: item.sort_order })),
    ];

    try {
      const response = await pageService.updatePageOrder(allOrders);
      if (response.success) {
        toast.success(`${category} order updated successfully`);
      } else {
        toast.error(response.message || "Failed to update order");
        fetchPages(); // Revert
      }
    } catch (error) {
      toast.error("Failed to update order");
      fetchPages(); // Revert
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 min-h-[60vh]">
        <Loader2 className="animate-spin text-primary mb-4" size={40} />
        <p className="text-slate-500 font-medium">Loading pages...</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate("/pages")}
          className="rounded-full h-10 w-10 p-0 hover:bg-slate-100"
        >
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-primary/10 text-primary rounded-2xl">
              <ArrowUpDown size={28} />
            </span>
            Reorder Pages
          </h1>
          <p className="text-slate-500 mt-1 font-medium">
            Drag and drop to rearrange pages within each category. The sequence will reflect in the main menu or footer.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="capabilities" className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-[600px] mb-8 bg-slate-100 p-1 rounded-xl">
          <TabsTrigger
            value="capabilities"
            className="rounded-lg py-2.5 transition-all text-slate-600 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm font-semibold"
          >
            Capabilities ({capabilities.length})
          </TabsTrigger>
          <TabsTrigger
            value="industries"
            className="rounded-lg py-2.5 transition-all text-slate-600 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm font-semibold"
          >
            Industries ({industries.length})
          </TabsTrigger>
          <TabsTrigger
            value="general"
            className="rounded-lg py-2.5 transition-all text-slate-600 data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm font-semibold"
          >
            General ({generalPages.length})
          </TabsTrigger>
        </TabsList>

        <DragDropContext onDragEnd={onDragEnd}>
          {/* Capabilities Tab Content */}
          <TabsContent value="capabilities">
            <Droppable droppableId="Capabilities">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {capabilities.map((page, index) => (
                    <Draggable
                      key={page.id}
                      draggableId={String(page.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            "bg-white border p-4 rounded-2xl flex items-center gap-4 transition-all",
                            snapshot.isDragging
                              ? "shadow-lg ring-2 ring-primary border-transparent"
                              : "border-slate-200 hover:border-primary/20 hover:shadow-md"
                          )}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600 p-1"
                          >
                            <GripVertical size={20} />
                          </div>

                          <div className="flex-1 flex items-center gap-3">
                            <span className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                              <FileText size={18} />
                            </span>
                            <div>
                              <p className="font-semibold text-slate-800">{page.page_name}</p>
                              <p className="text-xs text-slate-500 font-mono">/{page.slug}</p>
                            </div>
                          </div>

                          <div className="text-xs font-semibold px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
                            Position: {index + 1}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {capabilities.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                      <p className="text-slate-400">No pages found in Capabilities category.</p>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </TabsContent>

          {/* Industries Tab Content */}
          <TabsContent value="industries">
            <Droppable droppableId="Industries">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {industries.map((page, index) => (
                    <Draggable
                      key={page.id}
                      draggableId={String(page.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            "bg-white border p-4 rounded-2xl flex items-center gap-4 transition-all",
                            snapshot.isDragging
                              ? "shadow-lg ring-2 ring-primary border-transparent"
                              : "border-slate-200 hover:border-primary/20 hover:shadow-md"
                          )}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600 p-1"
                          >
                            <GripVertical size={20} />
                          </div>

                          <div className="flex-1 flex items-center gap-3">
                            <span className="p-2 bg-purple-50 text-purple-600 rounded-xl">
                              <FileText size={18} />
                            </span>
                            <div>
                              <p className="font-semibold text-slate-800">{page.page_name}</p>
                              <p className="text-xs text-slate-500 font-mono">/{page.slug}</p>
                            </div>
                          </div>

                          <div className="text-xs font-semibold px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
                            Position: {index + 1}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {industries.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                      <p className="text-slate-400">No pages found in Industries category.</p>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </TabsContent>

          {/* General Tab Content */}
          <TabsContent value="general">
            <Droppable droppableId="General">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="space-y-3"
                >
                  {generalPages.map((page, index) => (
                    <Draggable
                      key={page.id}
                      draggableId={String(page.id)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={cn(
                            "bg-white border p-4 rounded-2xl flex items-center gap-4 transition-all",
                            snapshot.isDragging
                              ? "shadow-lg ring-2 ring-primary border-transparent"
                              : "border-slate-200 hover:border-primary/20 hover:shadow-md"
                          )}
                        >
                          <div
                            {...provided.dragHandleProps}
                            className="text-slate-400 cursor-grab active:cursor-grabbing hover:text-slate-600 p-1"
                          >
                            <GripVertical size={20} />
                          </div>

                          <div className="flex-1 flex items-center gap-3">
                            <span className="p-2 bg-emerald-50 text-emerald-600 rounded-xl">
                              <FileText size={18} />
                            </span>
                            <div>
                              <p className="font-semibold text-slate-800">{page.page_name}</p>
                              <p className="text-xs text-slate-500 font-mono">/{page.slug}</p>
                            </div>
                          </div>

                          <div className="text-xs font-semibold px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
                            Position: {index + 1}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}

                  {generalPages.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
                      <p className="text-slate-400">No pages found in General category.</p>
                    </div>
                  )}
                </div>
              )}
            </Droppable>
          </TabsContent>
        </DragDropContext>
      </Tabs>
    </div>
  );
};

export default PageReorder;
